import { ReactNode } from 'react'

import CodeIcon from '@heroicons/react/outline/CodeIcon'
import ColorSwatchIcon from '@heroicons/react/outline/ColorSwatchIcon'
import CubeIcon from '@heroicons/react/outline/CubeIcon'
import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon'
import FireIcon from '@heroicons/react/outline/FireIcon'
import GlobeAltIcon from '@heroicons/react/outline/GlobeAltIcon'
import HomeIcon from '@heroicons/react/outline/HomeIcon'
import KeyIcon from '@heroicons/react/outline/KeyIcon'
import PlayIcon from '@heroicons/react/outline/PlayIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import TemplateIcon from '@heroicons/react/outline/TemplateIcon'
import TerminalIcon from '@heroicons/react/outline/TerminalIcon'
import UploadIcon from '@heroicons/react/outline/UploadIcon'
import UserGroupIcon from '@heroicons/react/outline/UserGroupIcon'
import UsersIcon from '@heroicons/react/outline/UsersIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'

import { I18nShape } from '@tribeplatform/react-components/i18n'

export type OnboardingModalStep = {
  banner?: string
  video?: string
  name: string
  description: string
  url?: string
}

export type OnboardingModal = {
  steps: OnboardingModalStep[]
  finishText?: string
  finishUrl?: string
}

export type OnboardingStep = {
  id: string
  name: string
  description: string
  icon: ReactNode
  buttonText?: string
  modal?: OnboardingModal
  autoComplete?: boolean
  time?: number
}

export type OnboardingSection = {
  id: string
  name: string
  children: OnboardingStep[]
}

export const sectionsFactory: (
  intl: I18nShape,
) => OnboardingSection[] = intl => {
  const { $t } = intl
  return [
    {
      id: 'getting-started',
      name: $t({
        defaultMessage: 'Getting started',
        id: 'Admin.Onboarding.GettingStarted',
      }),
      children: [
        {
          id: 'create-community',
          name: $t({
            defaultMessage: 'Create your community',
            id: 'Admin.Onboarding.CreateCommunity',
          }),
          description: $t({
            defaultMessage:
              'The best way to get started is to quit talking and begin doing',
            id: 'Admin.Onboarding.CreateCommunity.Description',
          }),
          icon: HomeIcon,
        },
        {
          id: 'tribe-platform-video',
          name: $t({
            defaultMessage: 'Get to know {BRAND}',
            id: 'Admin.Onboarding.GetToKnow',
          }),
          description: $t({
            defaultMessage: 'See what you’ll be able to do with {BRAND}',
            id: 'Admin.Onboarding.GetToKnow.Description',
          }),
          icon: PlayIcon,
          buttonText: $t({
            defaultMessage: 'Play video',
            id: 'Admin.Onboarding.Action.PlayVideo',
          }),
          time: 10,
          modal: {
            steps: [
              {
                video:
                  'https://www.loom.com/embed/7f099c6725ea4ea99eba0532e3ed3dbd',
                name: $t({
                  defaultMessage: 'Get to know {BRAND}',
                  id: 'Admin.Onboarding.GetToKnow',
                }),
                description: $t({
                  defaultMessage:
                    'A quick demo of {BRAND} core features and app ecosystem — easy to start, breeze to customize.',
                  id: 'Admin.Onboarding.GetToKnow.VideoDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Mark as complete',
              id: 'Admin.Onboarding.Action.MarkAsComplete',
            }),
          },
        },
        {
          id: 'upload-logo',
          name: $t({
            defaultMessage: 'Upload your logo',
            id: 'Admin.Onboarding.Logo',
          }),
          description: $t({
            defaultMessage: 'Select an image and upload your logo',
            id: 'Admin.Onboarding.Logo.Description',
          }),
          icon: UploadIcon,
          buttonText: $t({
            defaultMessage: 'Upload logo',
            id: 'Admin.Onboarding.Action.UploadLogo',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/square-logo.jpg',
                name: $t({
                  defaultMessage: 'Square logo',
                  id: 'Admin.Onboarding.SquareLogo',
                }),
                description: $t({
                  defaultMessage:
                    'For square logos, use branding that has been designed to be viewed on a small scale, such as favicons or logos used on your social media. Square images don’t need to include the brand name.',
                  id: 'Admin.Onboarding.SquareLogo.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/wide-logo.jpg',
                name: $t({
                  defaultMessage: 'Wide logo',
                  id: 'Admin.Onboarding.WideLogo',
                }),
                description: $t({
                  defaultMessage:
                    'For wide rectangular logos, your image should include a brand title or name that makes it easy for a user to identify your community name. This logo will also be used on a desktop version.',
                  id: 'Admin.Onboarding.WideLogo.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Upload logo',
              id: 'Admin.Onboarding.Action.UploadLogo',
            }),
            finishUrl: '/manage/settings/network',
          },
        },
        {
          id: 'create-space',
          name: $t({
            defaultMessage: 'Create a space',
            id: 'Admin.Onboarding.CreateSpace',
          }),
          description: $t({
            defaultMessage:
              'You can create multiple spaces for different use-cases',
            id: 'Admin.Onboarding.CreateSpace.Description',
          }),
          icon: CubeIcon,
          buttonText: $t({
            defaultMessage: 'Create space',
            id: 'Admin.Onboarding.Action.CreateSpace',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/create-space.jpg',
                name: $t({
                  defaultMessage: 'What is Space?',
                  id: 'Admin.Onboarding.WhatIsSpace',
                }),
                description: $t({
                  defaultMessage:
                    'Spaces enable you to categorize content and group members. It gives users a place to cultivate and discuss a certain topic. It’s similar to Facebook Groups.',
                  id: 'Admin.Onboarding.WhatIsSpace.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/create-collection.jpg',
                name: $t({
                  defaultMessage: 'Organize spaces in collections',
                  id: 'Admin.Onboarding.UseCollections',
                }),
                description: $t({
                  defaultMessage:
                    'A Collection is a group of Spaces grouped into one place. You can create multiple collections to organize and manage spaces. It will also help you to tidy up your sidebar.',
                  id: 'Admin.Onboarding.UseCollections.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/organize-collections.jpg',
                name: $t({
                  defaultMessage: 'Organize the Collection',
                  id: 'Admin.Onboarding.OrganizeCollections',
                }),
                description:
                  'Collections can be organized and managed within the left sidebar. You can add Spaces directly inside a Collection.',
              },
              {
                banner: '/images/admin-onboarding/space-permissions.jpg',
                name: $t({
                  defaultMessage: 'Make spaces public or members only',
                  id: 'Admin.Onboarding.SpacePermissions',
                }),
                description: $t({
                  defaultMessage:
                    'Spaces have two privacy settings. They can be public or private. Additionally, you can make Spaces invite-only or limit who would be able to invite people to a Space.',
                  id: 'Admin.Onboarding.SpacePermissions.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Create a Space',
              id: 'Admin.Onboarding.Action.CreateASpace',
            }),
            finishUrl: '/manage/content/spaces',
          },
        },
        {
          id: 'invite-teammates',
          name: $t({
            defaultMessage: 'Invite your teammates',
            id: 'Admin.Onboarding.Invite',
          }),
          description: $t({
            defaultMessage: 'Invite other admins and moderators',
            id: 'Admin.Onboarding.Invite.Description',
          }),
          icon: UsersIcon,
          buttonText: $t({
            defaultMessage: 'Invite',
            id: 'Admin.Onboarding.Action.Invite',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/invite-teammates.jpg',
                name: $t({
                  defaultMessage: 'Invite your teammates',
                  id: 'Admin.Onboarding.Invite',
                }),
                description: $t({
                  defaultMessage:
                    'Building a community is a team effort. Get the help you need to manage and grow your community by inviting your teammates.',
                  id: 'Admin.Onboarding.Invite.LongDescription',
                }),
              },
              {
                banner: '/images/admin-onboarding/make-admin.jpg',
                name: $t({
                  defaultMessage: 'Make them admin or moderator',
                  id: 'Admin.Onboarding.Invite.Roles',
                }),
                description: $t({
                  defaultMessage:
                    'Admins can fully manage the community, assign roles and change others’ roles. Moderators can only access moderation tools to edit, accept and reject posts.',
                  id: 'Admin.Onboarding.Invite.Roles.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/manage-staff-seats.jpg',
                name: $t({
                  defaultMessage: 'Manage staff seats',
                  id: 'Admin.Onboarding.StaffSeats',
                }),
                description: $t({
                  defaultMessage:
                    'Easily add or reduce the number of admins and moderators for your community. Improve efficacy of your team and effectively allocate resources.',
                  id: 'Admin.Onboarding.StaffSeats.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Invite teammates',
              id: 'Admin.Onboarding.Action.InviteTeammates',
            }),
            finishUrl: '/manage/people/members',
          },
        },
      ],
    },
    {
      id: 'join-movement',
      name: $t({
        defaultMessage: 'Join the movement',
        id: 'Admin.Onboarding.JoinMovement',
      }),
      children: [
        {
          id: 'join-campfire',
          name: $t({
            defaultMessage: 'Join {BRAND} community',
            id: 'Admin.Onboarding.JoinCampfire',
          }),
          description: $t({
            defaultMessage: 'Join other amazing community builders',
            id: 'Admin.Onboarding.JoinCampfire.Description',
          }),
          icon: FireIcon,
          buttonText: $t({
            defaultMessage: 'Join',
            id: 'Admin.Onboarding.Action.Join',
          }),
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/bettermode-community.jpg',
                name: $t({
                  defaultMessage: 'Join {BRAND} community',
                  id: 'Admin.Onboarding.JoinCommunity',
                }),
                description: $t({
                  defaultMessage:
                    'Our very own community for {BRAND} customers like yourself. Access {BRAND} self-serve support materials and connect, share, and engage with other {BRAND} admins',
                  id: 'Admin.Onboarding.JoinCampfire.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Join {BRAND} community',
              id: 'Admin.Onboarding.JoinCommunity',
            }),
            finishUrl: 'http://bettermode.com/hub',
          },
        },
      ],
    },
    {
      id: 'prepare-launch',
      name: $t({
        defaultMessage: 'Prepare for the launch',
        id: 'Admin.Onboarding.Launch',
      }),
      children: [
        {
          id: 'permission-and-privacy',
          name: $t({
            defaultMessage: 'Permission & privacy settings',
            id: 'Admin.Onboarding.Privacy',
          }),
          description: $t({
            defaultMessage:
              'Terms of service URL, permission, and default spaces',
            id: 'Admin.Onboarding.Privacy.Description',
          }),
          icon: EyeOffIcon,
          buttonText: $t({
            defaultMessage: 'Go to settings',
            id: 'Admin.Onboarding.Action.GoToSettings',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/public-private-community.jpg',
                name: $t({
                  defaultMessage: 'Public or Private community',
                  id: 'Admin.Onboarding.CommunityAccess',
                }),
                description: $t({
                  defaultMessage:
                    'Allow only community members to view and browse the community, or make your community invite-only by setting who can invite others.',
                  id: 'Admin.Onboarding.CommunityAccess.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/default-spaces.jpg',
                name: $t({
                  defaultMessage: 'Default Spaces for members',
                  id: 'Admin.Onboarding.DefaultSpace',
                }),
                description: $t({
                  defaultMessage:
                    'Select which Spaces new members should automatically join once they become a member of the community and ensure that they will not miss any important information.',
                  id: 'Admin.Onboarding.DefaultSpace.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Go to settings',
              id: 'Admin.Onboarding.Action.GoToSettings',
            }),
            finishUrl: '/manage/settings/network',
          },
        },
        {
          id: 'connect-domain',
          name: $t({
            defaultMessage: 'Connect your domain',
            id: 'Admin.Onboarding.Domain',
          }),
          description: $t({
            defaultMessage: 'Connect an existing domain name to your community',
            id: 'Admin.Onboarding.Domain.Description',
          }),
          icon: GlobeAltIcon,
          buttonText: $t({
            defaultMessage: 'Connect',
            id: 'Admin.Onboarding.Action.Connect',
          }),
          autoComplete: false,
          time: 15,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/connect-domain.png',
                name: $t({
                  defaultMessage: 'Connect your domain',
                  id: 'Admin.Onboarding.Domain',
                }),
                description: $t({
                  defaultMessage:
                    'Prepare your domain to point it to your community by making few easy-to-follow changes to your DNS.',
                  id: 'Admin.Onboarding.Domain.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Connect',
              id: 'Admin.Onboarding.Action.Connect',
            }),
            finishUrl: '/manage/settings/domain',
          },
        },
        {
          id: 'login-methods',
          name: $t({
            defaultMessage: 'Login methods',
            id: 'Admin.Onboarding.LoginMethods',
          }),
          description: $t({
            defaultMessage: 'Select how users would register and login',
            id: 'Admin.Onboarding.LoginMethods.Description',
          }),
          icon: KeyIcon,
          buttonText: $t({
            defaultMessage: 'Login settings',
            id: 'Admin.Onboarding.Action.LoginSettings',
          }),
          autoComplete: false,
          time: 10,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/social-login.jpg',
                name: $t({
                  defaultMessage: 'Social media login',
                  id: 'Admin.Onboarding.SocialLogin',
                }),
                description: $t({
                  defaultMessage:
                    'Enable your community members to log in to the community using popular social networks like Facebook, LinkedIn, and Google.',
                  id: 'Admin.Onboarding.SocialLogin.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/sso-methods.jpg',
                name: $t({
                  defaultMessage: 'Single Sign-On methods',
                  id: 'Admin.Onboarding.Sso',
                }),
                description: $t({
                  defaultMessage:
                    'Enable members to log in to the community with their existing credentials for your app or website. {BRAND} supports Auth0, Okta, JWT, and custom SSO methods.',
                  id: 'Admin.Onboarding.Sso.Description',
                }),
              },
              {
                banner: '/images/admin-onboarding/sso-integrations.jpg',
                name: $t({
                  defaultMessage: 'Log in with third-party tools',
                  id: 'Admin.Onboarding.3rdPartySso',
                }),
                description: $t({
                  defaultMessage:
                    'Allow members to log in to the community with their existing credentials on membership software such as Outseta and Memberful.',
                  id: 'Admin.Onboarding.3rdPartySso.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Connect',
              id: 'Admin.Onboarding.Action.Connect',
            }),
            finishUrl: '/manage/settings/authentication',
          },
        },
        {
          id: 'profile-fields',
          name: $t({
            defaultMessage: 'Add member profile fields',
            id: 'Admin.Onboarding.MemberFields',
          }),
          description: $t({
            defaultMessage: 'Add new profile fields to members',
            id: 'Admin.Onboarding.MemberFields.Description',
          }),
          icon: UserGroupIcon,
          buttonText: $t({
            defaultMessage: 'Add fields',
            id: 'Admin.Onboarding.Action.AddFields',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/customize-member-profile.jpg',
                name: $t({
                  defaultMessage: 'Customize member profile fields',
                  id: 'Admin.Onboarding.CustomizeProfile',
                }),
                description: $t({
                  defaultMessage:
                    'Choose what information is displayed in your members’ profile page. Pick from a set of existing fields or create custom fields based on your business.',
                  id: 'Admin.Onboarding.CustomizeProfile.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Customize',
              id: 'Admin.Onboarding.Action.Customize',
            }),
            finishUrl: '/manage/people/fields',
          },
        },
        {
          id: 'look-and-feel',
          name: $t({
            defaultMessage: 'Customize look & feel',
            id: 'Admin.Onboarding.LookAndFeel',
          }),
          description: $t({
            defaultMessage: 'Colors, typography, & navigation',
            id: 'Admin.Onboarding.LookAndFeel.Description',
          }),
          icon: ColorSwatchIcon,
          buttonText: $t({
            defaultMessage: 'Customize',
            id: 'Admin.Onboarding.Action.Customize',
          }),
          autoComplete: false,
          time: 10,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/customize-theme.jpg',
                name: $t({
                  defaultMessage: 'Customize look & feel',
                  id: 'Admin.Onboarding.LookAndFeel',
                }),
                description: $t({
                  defaultMessage:
                    'Get started fast with a thoughtfully crafted theme and customize based on your brand. Change color palettes, style, and typography to make your community yours.',
                  id: 'Admin.Onboarding.LookAndFeel.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Customize',
              id: 'Admin.Onboarding.Action.Customize',
            }),
            finishUrl: '/manage/customizer',
          },
        },
        {
          id: 'explore-apps',
          name: $t({
            defaultMessage: 'Explore apps & integrations',
            id: 'Admin.Onboarding.AppsAndIntegrations',
          }),
          description: $t({
            defaultMessage: 'Extend the capability of your community',
            id: 'Admin.Onboarding.AppsAndIntegrations.Description',
          }),
          icon: ViewGridIcon,
          buttonText: $t({
            defaultMessage: 'Explore',
            id: 'Admin.Onboarding.Action.Explore',
          }),
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/explore-apps.jpg',
                name: $t({
                  defaultMessage: 'Explore apps',
                  id: 'Admin.Onboarding.Apps',
                }),
                description: $t({
                  defaultMessage:
                    '{BRAND} App Store includes a growing number of apps that can be installed to unlock additional functionalities and enhance your community.',
                  id: 'Admin.Onboarding.Apps.Description',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Explore',
              id: 'Admin.Onboarding.Action.Explore',
            }),
            finishUrl: '/manage/apps',
          },
        },
        {
          id: 'invite-members',
          name: $t({
            defaultMessage: 'Invite members',
            id: 'Admin.Onboarding.Members',
          }),
          description: $t({
            defaultMessage: 'Time to start inviting members to your community',
            id: 'Admin.Onboarding.Members.Description',
          }),
          icon: UsersIcon,
          buttonText: $t({
            defaultMessage: 'Invite',
            id: 'Admin.Onboarding.Action.Invite',
          }),
          autoComplete: false,
          time: 10,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/explore-apps.jpg',
                name: $t({
                  defaultMessage: 'Invite members',
                  id: 'Admin.Onboarding.Members',
                }),
                description: $t({
                  defaultMessage:
                    'Invite community members and share what you have built. Get early feedback once you complete the setup and testing.',
                  id: 'Admin.Onboarding.Members.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Invite members',
              id: 'Admin.Onboarding.Action.InviteMembers',
            }),
            finishUrl: '/manage/people/members',
          },
        },
      ],
    },
    {
      id: 'next-level',
      name: $t({
        defaultMessage: 'Take it to next level',
        id: 'Admin.Onboarding.NextLevel',
      }),
      children: [
        {
          id: 'customize-spaces',
          name: $t({
            defaultMessage: 'Customize spaces',
            id: 'Admin.Onboarding.CustomizeSpaces',
          }),
          description: $t({
            defaultMessage: 'Add space blocks',
            id: 'Admin.Onboarding.CustomizeSpaces.Description',
          }),
          icon: TemplateIcon,
          buttonText: $t({
            defaultMessage: 'Customize',
            id: 'Admin.Onboarding.Action.Customize',
          }),
          autoComplete: false,
          time: 15,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/customize-spaces.jpg',
                name: $t({
                  defaultMessage: 'Customize spaces',
                  id: 'Admin.Onboarding.CustomizeSpaces',
                }),
                description: $t({
                  defaultMessage:
                    'Visually customize Spaces without touching a line of code. Select, arrange, and configure the Blocks for different use cases.',
                  id: 'Admin.Onboarding.CustomizeSpaces.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Customize',
              id: 'Admin.Onboarding.Action.Customize',
            }),
            finishUrl: '/manage/customizer/spaces',
          },
        },
        {
          id: 'moderation-rules',
          name: $t({
            defaultMessage: 'Moderation rules',
            id: 'Admin.Onboarding.Moderation',
          }),
          description: $t({
            defaultMessage: 'Set rules on comments or post moderation',
            id: 'Admin.Onboarding.Moderation.Description',
          }),
          icon: ShieldCheckIcon,
          buttonText: $t({
            defaultMessage: 'Set rules',
            id: 'Admin.Onboarding.Action.SetRules',
          }),
          autoComplete: false,
          time: 5,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/moderation-rules.jpg',
                name: $t({
                  defaultMessage: 'Moderation rules',
                  id: 'Admin.Onboarding.Moderation',
                }),
                description: $t({
                  defaultMessage:
                    'Prevent members from posting profane words with keyword blocklist. Use post-hiding capability to keep your community clean.',
                  id: 'Admin.Onboarding.Moderation.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Set rules',
              id: 'Admin.Onboarding.Action.SetRules',
            }),
            finishUrl: '/manage/settings/moderation',
          },
        },
        {
          id: 'embed-community',
          name: $t({
            defaultMessage: 'Embed your community',
            id: 'Admin.Onboarding.EmbedCommunity',
          }),
          description: $t({
            defaultMessage: 'Put part of community on your website or app',
            id: 'Admin.Onboarding.EmbedCommunity.Description',
          }),
          icon: CodeIcon,
          buttonText: $t({
            defaultMessage: 'Embed',
            id: 'Admin.Onboarding.Action.Embed',
          }),
          time: 30,
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/embed-community.jpg',
                name: $t({
                  defaultMessage: 'Embed your community',
                  id: 'Admin.Onboarding.EmbedCommunity',
                }),
                description: $t({
                  defaultMessage:
                    'Use embed tools to offer community experience right inside your website, web, or mobile app. Improve engagement and acquire new members.',
                  id: 'Admin.Onboarding.EmbedCommunity.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Embed community',
              id: 'Admin.Onboarding.Action.EmbedCommunity',
            }),
            finishUrl:
              'https://developers.bettermode.com/docs/guide/embedding/getting-started',
          },
        },
        {
          id: 'build-app',
          name: $t({
            defaultMessage: 'Build your own app',
            id: 'Admin.Onboarding.BuildApp',
          }),
          description: $t({
            defaultMessage: 'Create your very own custom app',
            id: 'Admin.Onboarding.BuildApp.Description',
          }),
          icon: TerminalIcon,
          buttonText: $t({
            defaultMessage: 'Build',
            id: 'Admin.Onboarding.Action.Build',
          }),
          modal: {
            steps: [
              {
                banner: '/images/admin-onboarding/build-app.jpg',
                name: $t({
                  defaultMessage: 'Build your own app',
                  id: 'Admin.Onboarding.BuildApp',
                }),
                description: $t({
                  defaultMessage:
                    'Create custom apps, integrations, and automations to extend the capability of the community the way you need.',
                  id: 'Admin.Onboarding.BuildApp.LongDescription',
                }),
              },
            ],
            finishText: $t({
              defaultMessage: 'Build',
              id: 'Admin.Onboarding.Action.Build',
            }),
            finishUrl:
              'https://developers.bettermode.com/docs/guide/apps/getting-started',
          },
        },
        // {
        //   id: 'understand-plans',
        //   name: 'Understand plans',
        //   description: 'Choose a right plan for your need',
        //   icon: PlayIcon,
        //   buttonText: 'View plans',
        // },
      ],
    },
  ]
}
